import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { STATE } from 'app/constants';
import { actionCreators } from './actions';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { ReportId, savedReportTranslation } from 'app/area/saved-reports/constants';
import { FullscreenCodeSelector } from 'app/components';
import { routeConstants } from 'app/routes/route-constants';
import { replace, toUpdateSavedReport } from 'app/area/shared/navigation';
import { SelectOptionValue, SelectOptions } from 'app/area/code-selector/types';
import { PromptConfirmation } from 'ui';
import { generateCopyLinkSearch, generateCopyLinkCompanyReport } from 'app/area/saved-reports/generate-copy-link';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { simpleFieldForPath, FIELD_TYPE, withSimpleForm, SimpleFormValidation } from 'app/infrastructure/simple-form';
import { form, endpoint } from './constants';
import { SerializeType } from 'app/infrastructure/simple-form/types';
import { CreateFolder } from 'app/area/saved-reports/create-folder/create-folder';
import { copyToClipboard, replaceTemplateString, insertOptionalWordbreaks } from 'app/infrastructure/platform';
import { NewFolderData } from 'app/area/saved-reports/types';
import { MdGroup, MdInsertDriveFile } from 'react-icons/md';
import get from 'lodash/get';

import styles from './create-report.scss';
import { Tooltip } from 'ui/common/tooltip/tooltip';
import { EditFolder } from 'app/area/saved-reports/edit-folder/edit-folder';
import { FavouriteDefinition } from 'app/types-shared';

interface SelectSavedReportProps extends DefaultContainerProps {
  loadSavedReports: () => void;
  removeReport: (id:number, title:string, unshare?:boolean) => void;
  moveReport: (id:number, parentFolderId:number) => void;
  loadFolderData: (id:number) => void;
  clearFolderData: () => void;
  folderDefinition: FavouriteDefinition;
  fetchingFolder: boolean;
  reports: SelectOptions;
  copyLinkNotification: (isSuccess: boolean) => void;
  formValues: any;
  fetchingCreateFolder: boolean;
  validate: SimpleFormValidation;
  saveFolder: (data: NewFolderData) => void;
  searchRequiredUsers?: boolean;
  reportCategories?: number[];
  folderType?: number;
  selectedReport?: number;
}

interface SelectSavedReportState {
  itemToDelete?: SelectOptionValue;
  itemToMove?: SelectOptionValue;
}

const SimpleForm = simpleFieldForPath(STATE.savedReport);

const createMenuItem = (title:string, onClick, key:number) => <li key={key}>
  <button type="button" className="dropdown-item" onClick={onClick}>
    {title}
  </button>
</li>;

class SelectSavedReportSelector extends React.Component<SelectSavedReportProps & typeof actionCreators, SelectSavedReportState> {
  public static statePath: string = STATE.savedReport;

  public state: Readonly<SelectSavedReportState> = {
    itemToDelete: null,
    itemToMove: null
  };

  public componentDidMount () {
    this.props.getSearchRequiredUsers && this.props.getSearchRequiredUsers();
  }

  private handleRequestDelete = (item: SelectOptionValue) => {
    this.setState({
      itemToDelete: item
    });
  }

  private handleCancelDelete = () => {
    this.setState({
      itemToDelete: null
    });
  }

  private handleConfirmDelete = () => {
    const shared = this.state.itemToDelete.owner.value !== this.props.sharedState.id;
    const {removeReport, reportCategories, folderType} = this.props;
    const {itemToDelete} = this.state;
    removeReport(itemToDelete.value, itemToDelete.description, shared, reportCategories, folderType);
    this.setState({
      itemToDelete: null
    });
  }

  private renderItemLabel = (item: SelectOptionValue) => {
    const { sharedState, translation } = this.props;
    const { owner, sharedThroughUserGroup } = item;
    const owned = item.owner.value === sharedState.id;
    const isFolder = item.descendants;

    if (owned) {
      return null;
    }

    const message = sharedThroughUserGroup ?
      (isFolder ? translation.shared_folder_group : translation.shared_report_group) :
      (isFolder ? translation.shared_folder_user : translation.shared_report);

    const label = <div className={styles.label}>{translation.shared} {sharedThroughUserGroup && <MdGroup/>}</div>;

    if (owner.value === 0) {
      return label;
    }

    return <Tooltip center={true} title={replaceTemplateString(message, owner.description)}>
      {label}
    </Tooltip>;
  }

  private renderItemDropdown = (item: SelectOptionValue) => {
    const {translation, copyLinkNotification, sharedState} = this.props;

    const owned = item.owner.value === sharedState.id;

    const deleteClick = () => {
      this.handleRequestDelete(item);
    };

    const copyLink = () => {
      const link = this.isCompanyReport() ? generateCopyLinkCompanyReport(item.value): generateCopyLinkSearch(item.value);
      const copyStatus = copyToClipboard(link);
      copyLinkNotification(copyStatus);
    };

    const moveReport = () => {
      this.setState({itemToMove: item});
    };

    const editReport = () => {
      if(this.isCompanyReport()) {
        this.props.toUpdateCompanyReport(item.value);
      }
      else { 
        toUpdateSavedReport(item.value);
      }
    };

    const editFolder = () => {
      this.props.loadFolderData(item.value);
    };

    const menuItems = [
      (owned || !item.sharedThroughUserGroup) &&
        createMenuItem(item.descendants ? translation.remove_folder : translation.remove_report, deleteClick, 2),
      owned &&
        createMenuItem(item.descendants ? translation.move_folder : translation.move_report, moveReport, 3),
      owned && !item.descendants &&
        createMenuItem(translation.edit_report, editReport, 4),
      owned && item.descendants &&
        createMenuItem(translation.edit_folder, editFolder,4),
      !item.descendants &&
        createMenuItem(translation.copy_to_clipboard, copyLink, 5)
    ].filter(i => i);

    return menuItems.length !== 0 ? menuItems : null;
  }

  private isCompanyReport = (): boolean => {
    const {selectedReport} = this.props;
    return !!(selectedReport === ReportId.CompanyReport);
  }

  private moveFolder = () => {

    const selectedFolderId = get(this, `props.formValues.${form.folder}.value`);
    const id = get(this, 'state.itemToMove.value');
    const {moveReport, reportCategories, folderType} = this.props;

    moveReport(id, selectedFolderId, reportCategories, folderType);
  }

  private closeMoveFolder = () => {
    this.setState({
      itemToMove: null
    });
  }

  private closeEditFolder = () => {
    this.props.clearFolderData();
  }

  private loadSavedReports = () => {
    this.props.loadSavedReports(this.props.reportCategories, this.props.folderType);
  }

  public render () {
    const { translation, reports, folderDefinition, fetchingFolder, searchRequiredUsers, 
      reportCategories, folderType, selectedReport} = this.props;
    const {itemToDelete, itemToMove} = this.state;
    const modalTitle = this.isCompanyReport() ? translation.saved_company_reports_title : translation.saved_reports_title;
    const urlResolver = this.isCompanyReport() ? routeConstants.savedCompanyReportFor : routeConstants.savedReportFor;

    return <><FullscreenCodeSelector
      onOpen={this.loadSavedReports}
      onClose={navigate.toStartupView}
      fetching={reports.fetching}
      options={reports.items}
      title={modalTitle}
      urlResolver={urlResolver}
      textGoBack={translation.back}
      routeHandler={replace}
      itemDropdownRender={this.renderItemDropdown}
      itemLabelRender={this.renderItemLabel}
      defaultIcon={<MdInsertDriveFile className={`${styles.selectCodeBtnIcon} ${styles.defaultIcon}`}/>}
      showAllOptionsWhenSingleFolderExists={false}
    />
    {itemToDelete && <PromptConfirmation
      title={itemToDelete.descendants ? translation.remove_folder : translation.remove_report}
      cancelText={translation.cancel}
      continueText={itemToDelete.descendants ? translation.remove_folder : translation.remove_report}
      description={
        insertOptionalWordbreaks(replaceTemplateString(
          itemToDelete.descendants ? translation.confirm_remove_folder : translation.confirm_remove,
          `"${itemToDelete.description}"`))
        }
      onCancel={this.handleCancelDelete}
      onContinue={this.handleConfirmDelete}/>
    }
    {(fetchingFolder || folderDefinition) &&
        <EditFolder
          onClose={this.closeEditFolder}
          definition={folderDefinition}
          searchRequiredUsers={searchRequiredUsers}
          reportCategories={reportCategories}
          folderType={folderType}
          {...this.props}
        />}

    {itemToMove && <ModalWrapper
      onClose={this.closeMoveFolder}
      onDone={this.moveFolder}
      closeOnDone={true}
      btnTitle={itemToMove.descendants ? translation.move_folder : translation.move_report}
      headerTitle={itemToMove.descendants ? translation.move_folder : translation.move_report}
      >
        <SimpleForm
          className={styles.createFolder}
          name={form.folder}
          type={FIELD_TYPE.SELECT_SINGLE_HIERARCHY}
          optionsEndpoint={endpoint.loadFolderOptions}
          optionsParams={{folderType}}
          label={{ text: translation.folder }}
          initialValue={''}
          translation={translation}
          placeholder={translation.search_placeholder}
          serialize={{ type: SerializeType.V_VALUE }} />
        <CreateFolder
          className={styles.createFolder}
          {...this.props}
          translation={translation}
          fetching={this.props.fetchingCreateFolder} 
          selectedReport={selectedReport} />
    </ModalWrapper>}

    </>;
  }
}

const container = containerFactory(withSimpleForm(SelectSavedReportSelector), actionCreators, undefined, savedReportTranslation);

export {container as SelectSavedReportSelector};
